import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import cn from "classnames";
import LearnShell from "./LearnShell";
import CourseCard from "./CourseCard";
import { fetchCourses, FORMAT_LABEL, progressPct, cheapestPackageFor, priceFor } from "./learnApi";

const FORMATS = ["course", "playlist", "series", "guide"];
const LEVELS = ["Foundational", "Intermediate", "Advanced"];
const MINE = [
  { key: "saved", label: "Saved" },
  { key: "in_progress", label: "In progress" },
  { key: "completed", label: "Completed" },
];

const LearnLibrary = () => {
  const [params, setParams] = useSearchParams();
  const [courses, setCourses] = useState(null);
  const [packages, setPackages] = useState([]);
  const [isAttendee, setIsAttendee] = useState(false);
  const [topics, setTopics] = useState([]);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCourses()
      .then((data) => { if (!cancelled) { setCourses(data.courses || []); setTopics(data.topics || []); setPackages(data.packages || []); setIsAttendee(Boolean(data.viewer?.isAttendee)); } })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, []);

  const q = params.get("q") || "";
  const topic = params.get("topic") || "";
  const formats = params.getAll("format");
  const levels = params.getAll("level");
  const mine = params.getAll("mine");
  const activeCount = (topic ? 1 : 0) + formats.length + levels.length + mine.length;

  const update = (mutate) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };
  const toggleMulti = (key, value) => update((next) => {
    const current = next.getAll(key);
    next.delete(key);
    (current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
      .forEach((v) => next.append(key, v));
  });
  const setTopic = (value) => update((next) => { if (value) next.set("topic", value); else next.delete("topic"); });
  const setQuery = (value) => update((next) => { if (value) next.set("q", value); else next.delete("q"); });
  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const filtered = useMemo(() => {
    if (!courses) return [];
    const needle = q.trim().toLowerCase();
    return courses.filter((c) => {
      if (topic && c.topic !== topic) return false;
      if (formats.length && !formats.includes(c.format)) return false;
      if (levels.length && !levels.includes(c.level)) return false;
      if (mine.length) {
        const pct = progressPct(c);
        const ok = mine.some((m) =>
          (m === "saved" && c.is_saved) ||
          (m === "in_progress" && pct > 0 && pct < 100) ||
          (m === "completed" && pct >= 100)
        );
        if (!ok) return false;
      }
      if (needle) {
        const hay = [c.title, c.subtitle, c.description, c.topic, c.instructor_name].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [courses, q, topic, formats, levels, mine]);

  const Filters = (
    <aside className={cn("learn-filters", { "learn-filters--open": filtersOpen })}>
      <div className="learn-filters__head">
        <h2>Filters</h2>
        {activeCount > 0 && (
          <button type="button" className="learn-filters__clear" onClick={clearAll}>Clear ({activeCount})</button>
        )}
      </div>

      <div className="learn-filters__group">
        <h3>Topic</h3>
        <button type="button" className={cn("learn-filters__option", { active: !topic })} onClick={() => setTopic("")}>All topics</button>
        {topics.map((t) => (
          <button key={t} type="button" className={cn("learn-filters__option", { active: topic === t })} onClick={() => setTopic(t)}>{t}</button>
        ))}
      </div>

      <div className="learn-filters__group">
        <h3>Format</h3>
        {FORMATS.map((f) => (
          <label key={f} className="learn-filters__check">
            <input type="checkbox" checked={formats.includes(f)} onChange={() => toggleMulti("format", f)} />
            <span>{FORMAT_LABEL[f]}</span>
          </label>
        ))}
      </div>

      <div className="learn-filters__group">
        <h3>Level</h3>
        {LEVELS.map((l) => (
          <label key={l} className="learn-filters__check">
            <input type="checkbox" checked={levels.includes(l)} onChange={() => toggleMulti("level", l)} />
            <span>{l}</span>
          </label>
        ))}
      </div>

      <div className="learn-filters__group">
        <h3>My content</h3>
        {MINE.map((m) => (
          <label key={m.key} className="learn-filters__check">
            <input type="checkbox" checked={mine.includes(m.key)} onChange={() => toggleMulti("mine", m.key)} />
            <span>{m.label}</span>
          </label>
        ))}
      </div>
    </aside>
  );

  return (
    <LearnShell redirectWhenComingSoon>
      <div className="learn-container learn-library">
        <header className="learn-library__head">
          <div>
            <Link to="/learn" className="learn-back">← Learning Hub</Link>
            <h1 className="learn-page-title">Library</h1>
            <p className="learn-page-subtitle">
              Every course, playlist, series and guide in one place.
            </p>
          </div>
          <label className="learn-search">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.6"/><path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            <input
              type="search"
              placeholder="Search topics, instructors, lessons"
              value={q}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
        </header>

        <div className="learn-chips learn-chips--library" aria-label="Topics">
          <button type="button" className={cn("learn-chip", { "learn-chip--active": !topic })} onClick={() => setTopic("")}>All</button>
          {topics.map((t) => (
            <button key={t} type="button" className={cn("learn-chip", { "learn-chip--active": topic === t })} onClick={() => setTopic(t)}>{t}</button>
          ))}
          <button
            type="button"
            className={cn("learn-chip learn-chip--filters", { "learn-chip--active": filtersOpen })}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filters{activeCount - (topic ? 1 : 0) > 0 ? ` · ${activeCount - (topic ? 1 : 0)}` : ""}
          </button>
        </div>

        <div className="learn-library__layout">
          {Filters}
          <section className="learn-library__results">
            <div className="learn-library__count">
              {courses ? `${filtered.length} ${filtered.length === 1 ? "title" : "titles"}` : "Loading…"}
              {activeCount > 0 && (
                <button type="button" className="learn-filters__clear" onClick={clearAll}>Clear filters</button>
              )}
            </div>
            {error && <div className="learn-error">We couldn't load the library right now. Please refresh.</div>}
            {courses && filtered.length === 0 && (
              <div className="learn-empty">
                <h3>Nothing matches those filters yet.</h3>
                <p>Try a different topic or clear your filters.</p>
              </div>
            )}
            <div className="learn-grid">
              {filtered.map((c) => (
                <CourseCard key={c.id} course={c} priceFrom={c.locked ? priceFor(cheapestPackageFor(c, packages, isAttendee), isAttendee) : null} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </LearnShell>
  );
};

export default LearnLibrary;
